import { z } from "zod";

const CompanyFileIdSchema = z.object({
  fileType: z.string(),
  fileId: z.string(),
});

const CompanyFileSchema = z.object({
  _id: z.string(),
  company: z.string(),
  file_type: z.string(),
  file_url: z.string(),
  file_size: z.string(),
  file_name: z.string().optional(),
  time_stamp: z.string(),
  upload_source: z.enum(["local", "google_drive", "docsend", "email"]).optional(),
});

const OwnerSchema = z.object({
  _id: z.string(),
  user_type: z.string(),
  country: z.string(),
  isVerified: z.boolean(),
  created_a_company_once: z.boolean(),
  receive_follow_up_registration_email: z.boolean(),
  status: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
  created_on: z.string(),
  __v: z.number(),
});

export const DealInfoSchema = z.object({
  field_name: z.string(),
  field_value: z.union([z.string(), z.array(z.string()), z.number()]),
  field_state: z.string(),
  sources: z
    .array(
      z.object({
        text: z.string(),
        created_on: z.string(),
        file_type: z.string(),
        source_type: z.string(),
        heading: z.string(),
        file_url: z.string(),
        category: z.array(z.string()),
        source_description: z.string(),
      })
    )
    .optional(),
  suggestions: z
    .array(
      z.object({
        url: z.string(),
        title: z.string().optional(),
        description: z.string().optional(),
        favicon: z.string().optional(),
      })
    )
    .optional(),
});

const StartupScoreSchema = z.object({
  Score: z.number(),
  Reason: z.string(),
});

const StartupScoresSchema = z.object({
  _id: z.string(),
  startupscore: z.object({
    Idea: StartupScoreSchema,
    Tech: StartupScoreSchema,
    Team: StartupScoreSchema,
    Marketing: StartupScoreSchema,
    Traction: StartupScoreSchema,
    Overall: StartupScoreSchema,
    _id: z.string(),
  }),
  created_on: z.string(),
  credits_used: z.number(),
});

const MarketIntelligenceSchema = z.object({
  _id: z.string(),
  market_intel: z.object({
    Market_Summary: z.object({
      Key_Market_Drivers: z.array(z.string()),
      Competitive_Landscape: z.string(),
      Growth_Opportunities: z.array(z.string()),
    }),
    Market_Size: z.object({
      TAM_size: z.number(),
      TAM_potential: z.array(z.string()),
      TAM_scope: z.array(z.string()),
      TAM_context: z.string(),
      SAM_size: z.number(),
      SAM_potential: z.array(z.string()),
      SAM_scope: z.array(z.string()),
      SAM_context: z.string(),
      SOM_size: z.number(),
      SOM_potential: z.array(z.string()),
      SOM_scope: z.array(z.string()),
      SOM_context: z.string(),
    }),
  }),
  created_on: z.string(),
  credits_used: z.number(),
});

const DataSufficiencySchema = z.object({
  _id: z.string(),
  datasufficiencyscore: z.object({
    Idea: z.object({
      Score: z.number(),
      Reason: z.string(),
    }),
    Tech: z.object({
      Score: z.number(),
      Reason: z.string(),
    }),
    Team: z.object({
      Score: z.number(),
      Reason: z.string(),
    }),
    Marketing: z.object({
      Score: z.number(),
      Reason: z.string(),
    }),
    Traction: z.object({
      Score: z.number(),
      Reason: z.string(),
    }),
    Overall: z.object({
      Score: z.number(),
      Reason: z.string(),
    }),
    _id: z.string(),
  }),
  created_on: z.string(),
  credits_used: z.number(),
});

const DataSufficiencyV2SourceSchema = z.object({
  file_url: z.string(),
  file_type: z.string(),
  source_type: z.string(),
  source_description: z.union([z.string(), z.number()]),
});

const DataSufficiencyV2Schema = z.object({
  _id: z.string(),
  datasufficiencyscore_v2: z.object({
    // 1) Problem / solution
    "problem/solution": z
      .object({
        score_report: z.array(
          z.object({
            question: z.string(),
            score: z.number(),
            reasoning: z.string(),
            sources: z.array(DataSufficiencyV2SourceSchema).optional(),
          })
        ),
        final_score: z.number(),
        missing_report: z.array(
          z.object({
            question: z.string(),
            score: z.number(),
            reasoning: z.string(),
            sources: z.array(DataSufficiencyV2SourceSchema).optional(),
          })
        ),
      })
      .optional(),
    // 2) Product /service
    "product/service": z
      .object({
        score_report: z.array(
          z.object({
            question: z.string(),
            score: z.number(),
            reasoning: z.string(),
            sources: z.array(DataSufficiencyV2SourceSchema).optional(),
          })
        ),
        final_score: z.number(),
        missing_report: z.array(
          z.object({
            question: z.string(),
            score: z.number(),
            reasoning: z.string(),
            sources: z.array(DataSufficiencyV2SourceSchema).optional(),
          })
        ),
      })
      .optional(),
    // 3) Team
    team: z
      .object({
        score_report: z.array(
          z.object({
            question: z.string(),
            score: z.number(),
            reasoning: z.string(),
            sources: z.array(DataSufficiencyV2SourceSchema).optional(),
          })
        ),
        final_score: z.number(),
        missing_report: z.array(
          z.object({
            question: z.string(),
            score: z.number(),
            reasoning: z.string(),
            sources: z.array(DataSufficiencyV2SourceSchema).optional(),
          })
        ),
      })
      .optional(),
    // 4) Marketing
    marketing: z
      .object({
        score_report: z.array(
          z.object({
            question: z.string(),
            score: z.number(),
            reasoning: z.string(),
            sources: z.array(DataSufficiencyV2SourceSchema).optional(),
          })
        ),
        final_score: z.number(),
        missing_report: z.array(
          z.object({
            question: z.string(),
            score: z.number(),
            reasoning: z.string(),
            sources: z.array(DataSufficiencyV2SourceSchema).optional(),
          })
        ),
      })
      .optional(),
    // 5) Traction
    traction: z
      .object({
        score_report: z.array(
          z.object({
            question: z.string(),
            score: z.number(),
            reasoning: z.string(),
            sources: z.array(DataSufficiencyV2SourceSchema).optional(),
          })
        ),
        final_score: z.number(),
        missing_report: z.array(
          z.object({
            question: z.string(),
            score: z.number(),
            reasoning: z.string(),
            sources: z.array(DataSufficiencyV2SourceSchema).optional(),
          })
        ),
      })
      .optional(),
    // 6) Finance/ round
    "finance/round": z
      .object({
        score_report: z.array(
          z.object({
            question: z.string(),
            score: z.number(),
            reasoning: z.string(),
            sources: z.array(DataSufficiencyV2SourceSchema).optional(),
          })
        ),
        final_score: z.number(),
        missing_report: z.array(
          z.object({
            question: z.string(),
            score: z.number(),
            reasoning: z.string(),
            sources: z.array(DataSufficiencyV2SourceSchema).optional(),
          })
        ),
      })
      .optional(),
    // 7) Legal
    legal: z
      .object({
        score_report: z.array(
          z.object({
            question: z.string(),
            score: z.number(),
            reasoning: z.string(),
            sources: z.array(DataSufficiencyV2SourceSchema).optional(),
          })
        ),
        final_score: z.number(),
        missing_report: z.array(
          z.object({
            question: z.string(),
            score: z.number(),
            reasoning: z.string(),
            sources: z.array(DataSufficiencyV2SourceSchema).optional(),
          })
        ),
      })
      .optional(),
    overall_score: z.number().optional(),
  }),
  created_on: z.string(),
  credits_used: z.number(),
});

const SummarySchema = z.object({
  _id: z.string(),
  summary: z.object({
    _id: z.string(),
    Idea: z.string(),
    Tech: z.string(),
    Marketing: z.string(),
    Team: z.string(),
    BusinessModel: z.string(),
    Traction: z.string(),
  }),
  created_on: z.string(),
  credits_used: z.number(),
});

const TearSheetSchema = z.object({
  _id: z.string(),
  tearsheet: z.object({
    KeyHighlights: z.array(z.string()),
    Overview: z.string(),
    Problem: z.string(),
    Solution: z.string(),
    Market: z.string(),
    Traction: z.string(),
    Team: z.string(),
    Advantage: z.string(),
    DateCreated: z.string(),
  }),
  created_on: z.string(),
  credits_used: z.number(),
});

// Customize-UI tearsheet — one doc per opportunity_application_id in
// the separate `tearsheets` collection, written by the AI team's
// customize_ui_generation worker. `data[]` is the versions array.
const TearsheetV2VersionSchema = z.object({
  ui_data: z.any(),
  version_metadata: z.object({
    version_id: z.string(),
    version_name: z.string().optional(),
    // Tree linkage: the version this one was refined from. Root/original
    // versions have no parent (null or absent).
    parent_version_id: z.string().nullable().optional(),
  }),
});

const TearsheetV2Schema = z.object({
  _id: z.string(),
  opportunity_application_id: z.string(),
  report_type: z.string(),
  company_id: z.string(),
  pertains_to_opportunity: z.string(),
  data: z.array(TearsheetV2VersionSchema),
  created_on: z.string(),
  updated_on: z.string().optional(),
});

const MetricDataPointSchema = z.object({
  title: z.string(),
  value: z.union([z.string(), z.number()]),
});

const MetricSourceSchema = z.object({
  file_url: z.string(),
  file_type: z.string(),
  source_type: z.string(),
  source_description: z.string(),
});

const MetricItemSchema = z.object({
  title: z.string(),
  sub_category: z.string(),
  category: z.string(),
  sources: z.array(MetricSourceSchema),
  data: z.array(MetricDataPointSchema),
});

const MetricsSchema = z.object({
  _id: z.string(),
  metrics: z.array(MetricItemSchema),
  created_on: z.string(),
  credits_used: z.number().optional(),
});

const dealNoteSchema = z.object({
  _id: z.string(),
  note: z.string(),
  created_by_user: z.string(),
  created_by_user_id: z.string().optional(), // Optional for backward compatibility with old notes
  created_on: z.string(),
  tags: z.array(z.string()),
});

const ReviewSchema = z.object({
  _id: z.string(),
  applicant_company: z.string(),
  affiliated_opportunity: z.string(),
  judge: z.string(),
  idea: z.number(),
  idea_comment: z.string(),
  technology: z.number(),
  technology_comment: z.string(),
  marketing: z.number(),
  marketing_comment: z.string(),
  team: z.number(),
  team_comment: z.string(),
  overallRating: z.number(),
  feedback: z.string(),
  created_on: z.string(),
  __v: z.number(),
});

const ContentSchema = z.object({
  extracted: z.record(z.any()),
  anonymized: z.record(z.any()),
});

const ChatSchema = z.object({
  _id: z.string(),
  chat: z.array(
    z.object({
      username: z.string(),
      userQuestion: z.string(),
      aiResponse: z.string(),
      timestamp: z.string(),
    })
  ),
  created_on: z.string(),
  credits_used: z.number(),
});

const WebpageItemSchema = z.object({
  webpage_link: z.string(),
  file_url: z.string(),
  file_size: z.string(),
  timestamp: z.string(),
  summary: z.string(),
  summary_timestamp: z.string(),
});

const WebsiteSummaryV2Schema = z.object({
  _id: z.string(),
  webpage_list: z.array(WebpageItemSchema),
  website_summary: z.string(),
  main_webpage_link: z.string(),
  created_on: z.string(),
  credits_used: z.number().optional(),
});

const MarketResearchItemSchema = z.object({
  model: z.string(),
  Market_Summary: z.object({
    Key_Market_Drivers: z.array(z.string()),
    Competitive_Landscape: z.string(),
    Growth_Opportunities: z.array(z.string()),
  }),
  Market_Size: z.object({
    TAM_size: z.number(),
    TAM_potential: z.array(z.string()),
    TAM_scope: z.array(z.string()),
    TAM_context: z.string(),
    SAM_size: z.number(),
    SAM_potential: z.array(z.string()),
    SAM_scope: z.array(z.string()),
    SAM_context: z.string(),
    SOM_size: z.number(),
    SOM_potential: z.array(z.string()),
    SOM_scope: z.array(z.string()),
    SOM_context: z.string(),
  }),
  Competitive_Analysis: z.object({
    Competitor_Landscape: z.object({
      Direct_Competitors: z.array(z.string()),
      Indirect_Competitors: z.array(z.string()),
      Potential_Entrants: z.array(z.string()),
      Substitute_Products_Services: z.array(z.string()),
    }),
    Competitor_Profiles: z.array(
      z.object({
        Company_Overview: z.string(),
        Products_Services: z.array(z.string()),
        Target_Market: z.string(),
        Market_Positioning: z.string(),
        Strengths: z.array(z.string()),
        Weaknesses: z.array(z.string()),
      })
    ),
  }),
});

const MarketResearchV2Schema = z.object({
  _id: z.string(),
  market_research: z.array(MarketResearchItemSchema),
  market_research_final: MarketResearchItemSchema.nullable(),
  created_on: z.string(),
  credits_used: z.number().optional(),
});

const GapAnalysisQuestionSchema = z.object({
  score: z.number(),
  question: z.string(),
  category: z.string(),
  reasoning: z.string(),
  sources: z.array(z.any()).optional(),
  days_since_earliest: z.number().optional(),
  decay_factor: z.number().optional(),
  decay_description: z.string().optional(),
  doc_type_required: z.array(z.string()).optional(),
  description: z.string().optional(),
  type: z.string().optional(),
});

const GapAnalysisSchema = z.object({
  _id: z.string(),
  questions: z.array(GapAnalysisQuestionSchema),
  gap_analysis_score: z.number(),
  created_on: z.string(),
  credits_used: z.number().optional(),
});

const DealFitCategorySchema = z.object({
  score: z.number(),
  explanation: z.string(),
});

const DealFitSchema = z.object({
  _id: z.string(),
  deal_fit: z.object({
    address: DealFitCategorySchema.optional(),
    industry: DealFitCategorySchema.optional(),
    stage: DealFitCategorySchema.optional(),
    business_model: DealFitCategorySchema.optional(),
    fundraising: DealFitCategorySchema.optional(),
    overall: DealFitCategorySchema.optional(),
  }),
  created_on: z.string(),
  credits_used: z.number().optional(),
});

const ValuationsSchema = z.object({
  _id: z.string().optional(),
  valuationLow: z.number(),
  valuationMid: z.number(),
  valuationHigh: z.number(),
  cashRaised: z.number(),
  dilution: z.number(),
  valuationMethod: z.string(),
  startupScore: z.object({
    Idea: z
      .object({
        Score: z.number().optional(),
        Reason: z.string().optional(),
      })
      .optional(),
    Tech: z
      .object({
        Score: z.number().optional(),
        Reason: z.string().optional(),
      })
      .optional(),
    Team: z
      .object({
        Score: z.number().optional(),
        Reason: z.string().optional(),
      })
      .optional(),
    Marketing: z
      .object({
        Score: z.number().optional(),
        Reason: z.string().optional(),
      })
      .optional(),
    Traction: z
      .object({
        Score: z.number().optional(),
        Reason: z.string().optional(),
      })
      .optional(),
    Overall: z
      .object({
        Score: z.number().optional(),
        Reason: z.string().optional(),
      })
      .optional(),
  }),
  dataSufficiencyScore: z.object({
    Idea: z
      .object({
        Score: z.number().optional(),
        Reason: z.string().optional(),
      })
      .optional(),
    Tech: z
      .object({
        Score: z.number().optional(),
        Reason: z.string().optional(),
      })
      .optional(),
    Team: z
      .object({
        Score: z.number().optional(),
        Reason: z.string().optional(),
      })
      .optional(),
    Marketing: z
      .object({
        Score: z.number().optional(),
        Reason: z.string().optional(),
      })
      .optional(),
    Traction: z
      .object({
        Score: z.number().optional(),
        Reason: z.string().optional(),
      })
      .optional(),
    Overall: z
      .object({
        Score: z.number().optional(),
        Reason: z.string().optional(),
      })
      .optional(),
  }),
  stage: z.string(),
  created_on: z.string(),
});

const InsightSourceSchema = z.object({
  file_url: z.string(),
  file_type: z.string(),
  source_type: z.string(),
  source_description: z.union([z.string(), z.number()]),
  created_on: z.string(),
});

const EngineResultSchema = z.object({
  engine: z.string(),
  score: z.number(),
  explanation: z.string(),
});

const InsightSchema = z.object({
  question: z.string(),
  response: z.string(),
  sources: z.array(InsightSourceSchema),
  category: z.string(),
  engine_results: z.array(EngineResultSchema),
});

const InsightsReportSchema = z.object({
  _id: z.string(),
  insights: z.array(InsightSchema),
  created_on: z.string(),
  credits_used: z.number().optional(),
});

// Status values can be "pending", "completed", "in_progress", "failed",
// or "generating_*" (e.g. "generating_main_webpage_info", "generating_dealinfo")
const ReportStatusSchema = z.object({
  ingest: z.string().optional(),
  summary: z.string().optional(),
  tearsheet: z.string().optional(),
  datasufficiencyscore: z.string().optional(),
  datasufficiencyscore_v2: z.string().optional(),
  startupscore: z.string().optional(),
  deal_info: z.string().optional(),
  website_summary: z.string().optional(),
  website_summary_v2: z.string().optional(),
  website_summary_ingest: z.string().optional(),
  market_research: z.string().optional(),
  market_intel: z.string().optional(),
  metrics: z.string().optional(),
  valuations: z.string().optional(),
  linkedin_company_scrape: z.string().optional(),
  gap_analysis: z.string().optional(),
  deal_fit: z.string().optional(),
  insights: z.string().optional(),
});

const DealSchema = z.object({
  _id: z.string(),
  /**
   * Present on `fetch_single_deal`, whose `_id` is the company's. Absent on the
   * list endpoint, where `_id` is already the application's.
   */
  application_id: z.string().optional(),
  content: ContentSchema,
  opportunity_form: z.object({
    deal_notes: z.object({
      title: z.string(),
      helper_text: z.string(),
      display: z.boolean(),
      required: z.boolean(),
    }),
  }),
  company_type: z.array(z.string()),
  distinction: z.array(z.string()),
  industry: z.array(z.string()),
  country: z.string().nullable().optional(),
  ai_tracked: z.boolean(),
  // Computed server-side from the cached traffic series so the pipeline column
  // and the deal widget can never disagree. Null when no trend exists.
  website_traffic_growth: z
    .object({
      current: z.number().nullable(),
      current_month: z.string().nullable(),
      previous: z.number().nullable(),
      previous_month: z.string().nullable(),
      change_pct: z.number().nullable(),
      direction: z.enum(["up", "down", "flat"]).nullable(),
      previous_is_adjacent: z.boolean(),
    })
    .nullable()
    .optional(),
  raise_board: z.boolean().optional(),
  global_visibility: z.boolean().optional(),
  most_active_index: z.number(),
  manual_team_list: z.array(z.string()),
  lat: z.array(z.any()),
  long: z.array(z.any()),
  company_name: z.string(),
  founded_year: z.string(),
  month_of_inception: z.string(),
  year_of_inception: z.string(),
  company_file_ids: z.array(CompanyFileIdSchema),
  created_on: z.string(),
  __v: z.number(),
  address: z.string(),
  business_model: z.string(),
  company_website: z.string(),
  contact_phone: z.string(),
  stage: z.union([z.string(), z.number()]),
  advantage: z.string(),
  competitors: z.string(),
  growth: z.string(),
  incubator_accelerator_history: z.string(),
  market_size: z.string(),
  marketing_strategy: z.string(),
  objective: z.string(),
  problem: z.string(),
  raising_funds: z.string(),
  revenue_model: z.string(),
  revenue_since_inception: z.string(),
  short_bio: z.string(),
  solution: z.string(),
  target_market: z.string(),
  team_description: z.string(),
  traction: z.string(),
  questionnaire: z.array(
    z.object({
      question: z.string(),
      answer: z.string(),
    })
  ),
  reports_in_progress: z.object({
    deal_memo: z.boolean(),
  }),
  reviews: z.array(ReviewSchema),
  owner: OwnerSchema.optional().nullable(),
  comm_aff: z.array(z.any()),
  index: z.number(),
  company_files: z.array(CompanyFileSchema),
  aiGenerations: z.object({
    startupscores: z.array(StartupScoresSchema),
    datasufficiencyscores: z.array(DataSufficiencySchema),
    datasufficiencyscores_v2: z.array(DataSufficiencyV2Schema),
    market_intel: z.array(MarketIntelligenceSchema),
    summaries: z.array(SummarySchema),
    tearsheets: z.array(TearSheetSchema),
    tearsheets_v2: z.array(TearsheetV2Schema).optional(),
    metrics: z.array(MetricsSchema),
    valuations: z.array(ValuationsSchema),
    chats: z.array(ChatSchema),
    website_summary_v2: z.array(WebsiteSummaryV2Schema),
    market_research: z.array(MarketResearchV2Schema),
    gap_analysis: z.array(GapAnalysisSchema).optional(),
    deal_fit: z.array(DealFitSchema).optional(),
    insights: z.array(InsightsReportSchema).optional(),
  }),
  applicant_company: z.string(),
  pertains_to_opportunity: z.string(),
  action_type: z.string(),
  application_status: z.string(),
  deal_info: z.array(DealInfoSchema),
  deal_notes: z.array(dealNoteSchema).optional(),
  website_summary: z
    .object({
      website_summary: z.string(),
      created_on: z.string().optional(),
    })
    .nullable()
    .optional(),
  source: z.string().optional(),
  origin: z.string().nullable().optional(),
  company_source: z.string().optional(),
  applicationInfo: z.record(
    z.object({
      Title: z.string(),
      Answer: z.string(),
    })
  ),
  fundraising: z
    .object({
      total_amount: z.string(),
      committed_amount: z.string(),
      lead_investor: z.string(),
    })
    .optional(),
  fit_score: z
    .object({
      score: z.number(),
      breakdown: z.object({
        businessModel: z.object({
          score: z.number(),
          matches: z.array(z.string()),
          target: z.array(z.string()),
        }),
        stage: z.object({
          score: z.number(),
          matches: z.array(z.string()),
          target: z.array(z.string()),
        }),
        country: z.object({
          score: z.number(),
          matches: z.array(z.string()),
          target: z.array(z.string()),
        }),
        industry: z.object({
          score: z.number(),
          matches: z.array(z.string()),
          target: z.array(z.string()),
        }),
        distinction: z.object({
          score: z.number(),
          matches: z.array(z.string()),
          target: z.array(z.string()),
        }),
      }),
    })
    .optional(),
  prospective_aff_email: z.string().optional(),
  report_status: ReportStatusSchema.optional(),
});

export type Deal = z.infer<typeof DealSchema>;

export default DealSchema;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const dealTransformedSchema = z.object({
  id: z.string(),
  /**
   * The opportunity-application id.
   *
   * `id` is the *company*, which is how the rest of the client addresses a deal.
   * Anything stored per application rather than per company — analysis documents
   * are the first — needs this instead.
   */
  applicationId: z.string(),
  overview: z.object({
    name: z.string(),
    industry: z.array(z.string()),
    risk: z.number().nullable(),
    status: z.string(),
    addedOn: z.string(),
    website: z.string(),
    location: z.string().nullable(),
    country: z.string().nullable(),
    description: z.string().nullable(),
    businessModel: z.string().nullable(),
    companyStage: z.string(),
    founded: z.string().nullable(),
    websiteSummary: z
      .object({
        website_summary: z.string(),
        created_on: z.string().optional(),
      })
      .nullable()
      .optional(),
    linkedInInfo: z.any().nullable().optional(), // LinkedInCompanyData from gen-api.ts
    source: z.string(),
    origin: z.string().nullable().optional(),
    companySource: z.string().optional(),
  }),
  dealNotes: z.array(dealNoteSchema),
  aiTracked: z.boolean(),
  /** Depth this deal was analysed at. "full" for anything predating SAAS-915. */
  diligenceLevel: z.enum(["light", "full"]).default("full"),
  // Month-on-month website traffic growth, for the pipeline column. Null
  // whenever no trend can be derived - not opted in, no provider data, or only
  // one month of history.
  websiteTrafficGrowth: z
    .object({
      current: z.number().nullable(),
      current_month: z.string().nullable(),
      previous: z.number().nullable(),
      previous_month: z.string().nullable(),
      change_pct: z.number().nullable(),
      direction: z.enum(["up", "down", "flat"]).nullable(),
      previous_is_adjacent: z.boolean(),
    })
    .nullable()
    .optional(),
  /**
   * Answers for the workspace's AI columns, keyed by column id. Passthrough
   * because the keys are user-created and the value shape is the worker's.
   */
  aiColumnValues: z.record(z.any()).optional(),
  /** Curated view membership, so a list tab can reflect it without a refetch. */
  viewIds: z.array(z.string()).optional(),
  raiseBoard: z.boolean().optional(),
  globalVisibility: z.boolean().optional(),
  aiGenerations: z.object({
    datasufficiency: z.array(DataSufficiencySchema),
    datasufficiency_v2: z.array(DataSufficiencyV2Schema),
    summaries: z.array(SummarySchema),
    tearsheets: z.array(TearSheetSchema),
    tearsheets_v2: z.array(TearsheetV2Schema).optional(),
    // Unified list used by the panel + reports-tab dropdowns. Each
    // entry carries a `kind` discriminator for the renderer to pick
    // legacy vs. new-shape rendering.
    tearsheets_all: z.array(
      z.union([
        z.object({
          _id: z.string(),
          created_on: z.string(),
          kind: z.literal("legacy"),
          tearsheet: TearSheetSchema.shape.tearsheet,
        }),
        z.object({
          _id: z.string(),
          created_on: z.string(),
          kind: z.literal("v2"),
          data: TearsheetV2Schema.shape.data,
        }),
      ]),
    ),
    startupscores: z.array(StartupScoresSchema),
    metrics: z.array(MetricsSchema),
    valuations: z.array(ValuationsSchema),
    market_intel: z.array(MarketIntelligenceSchema),
    chats: z.array(ChatSchema),
    website_summary_v2: z.array(WebsiteSummaryV2Schema),
    market_research: z.array(MarketResearchV2Schema),
    gap_analysis: z.array(GapAnalysisSchema).optional(),
    deal_fit: z.array(DealFitSchema).optional(),
    insights: z.array(InsightsReportSchema).optional(),
    linkedInInfo: z
      .array(
        z.object({
          _id: z.string(),
          data: z.any().nullable(),
          created_on: z.string(),
        })
      )
      .optional(),
  }),
  questionnaire: z.array(
    z.object({
      question: z.string(),
      answer: z.string(),
    })
  ),
  dealInfo: z.object({
    deal: z.array(DealInfoSchema),
    applicationInfo: z.record(
      z.object({
        Title: z.string(),
        Answer: z.string(),
      })
    ),
  }),
  company_files: z.array(CompanyFileSchema),
  reviews: z.array(ReviewSchema),
  content: ContentSchema,
  fitScore: z
    .object({
      score: z.number(),
      breakdown: z.object({
        businessModel: z.object({
          score: z.number(),
          matches: z.array(z.string()),
          target: z.array(z.string()),
        }),
        stage: z.object({
          score: z.number(),
          matches: z.array(z.string()),
          target: z.array(z.string()),
        }),
        country: z.object({
          score: z.number(),
          matches: z.array(z.string()),
          target: z.array(z.string()),
        }),
        industry: z.object({
          score: z.number(),
          matches: z.array(z.string()),
          target: z.array(z.string()),
        }),
        distinction: z.object({
          score: z.number(),
          matches: z.array(z.string()),
          target: z.array(z.string()),
        }),
      }),
    })
    .optional(),
  prospectiveAffEmail: z.string().optional(),
  reportStatus: z
    .object({
      opportunityApplication: z
        .object({
          report_status: ReportStatusSchema.optional(),
        })
        .optional(),
    })
    .optional(),
});

export type DealTransformed = z.infer<typeof dealTransformedSchema>;
export type WebpageItem = z.infer<typeof WebpageItemSchema>;
export type WebsiteSummaryV2Item = z.infer<typeof WebsiteSummaryV2Schema>;
export type MarketResearchItem = z.infer<typeof MarketResearchItemSchema>;
export type MarketResearchV2Item = z.infer<typeof MarketResearchV2Schema>;
export type GapAnalysisItem = z.infer<typeof GapAnalysisSchema>;
export type GapAnalysisQuestion = z.infer<typeof GapAnalysisQuestionSchema>;
export type DealFitItem = z.infer<typeof DealFitSchema>;
export type InsightsReport = z.infer<typeof InsightsReportSchema>;
export type InsightItem = z.infer<typeof InsightSchema>;
